import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUIStore } from './ui'
import apiService from '../services/apiService'
import logService from '../services/logService'

export const useStatisticsStore = defineStore('statistics', () => {
  const uiStore = useUIStore()

  // State
  const statistics = ref([])
  const loading = ref(false)

  // API Functions
  const fetchStatistics = async () => {
    try {
      loading.value = true
      logService.info('Fetching statistics...')

      const response = await apiService.get('/statistics')

      if (response.success) {
        statistics.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch statistics')
      }
    } catch (error) {
      logService.error('Fetch Statistics Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return []
    } finally {
      loading.value = false
    }
  }

  const createStatistic = async (statisticData) => {
    try {
      loading.value = true
      logService.info('Creating statistic...', statisticData)

      const response = await apiService.post('/statistics', statisticData)

      if (response.success) {
        statistics.value.push(response.data)

        uiStore.showNotification({
          type: 'success',
          title: 'Statistic Created',
          message: 'Statistic has been created successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to create statistic')
      }
    } catch (error) {
      logService.error('Create Statistic Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Creation Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const updateStatistic = async (id, statisticData) => {
    try {
      loading.value = true
      logService.info(`Updating statistic: ${id}`, statisticData)

      const response = await apiService.put(`/statistics/${id}`, statisticData)

      if (response.success) {
        const index = statistics.value.findIndex(s => s.id === id)
        if (index !== -1) {
          statistics.value[index] = response.data
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Statistic Updated',
          message: 'Statistic has been updated successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to update statistic')
      }
    } catch (error) {
      logService.error(`Update Statistic Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const deleteStatistic = async (id) => {
    try {
      loading.value = true
      logService.info(`Deleting statistic: ${id}`)

      const response = await apiService.delete(`/statistics/${id}`)

      if (response.success) {
        statistics.value = statistics.value.filter(s => s.id !== id)

        uiStore.showNotification({
          type: 'success',
          title: 'Statistic Deleted',
          message: 'Statistic has been deleted successfully.'
        })

        return true
      } else {
        throw new Error(response.message || 'Failed to delete statistic')
      }
    } catch (error) {
      logService.error(`Delete Statistic Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Deletion Failed',
        message: error.message
      })
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    statistics,
    loading,

    // API Actions
    fetchStatistics,
    createStatistic,
    updateStatistic,
    deleteStatistic
  }
})
